import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Popover } from 'antd';
import { ROUTER_MY_STORE_BUNDLES, ROUTER_SETTINGS_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { CogIcon, TrashAltIcon } from 'assets/icons';
import { ReferenceInput } from 'components/ReferenceInput/ReferenceInput';
import PrivateComponent from 'composers/PrivateComponent';
import { getMyStoreEnabled } from 'domains/appContext/AppContext.store';
import {
  addCatalogLaborTimeByCode,
  addReferenceNumber,
  getCurrentEstimateId,
  getEstimateSettings,
  getEstimateVehicle,
} from 'domains/estimate/Estimate.store';
import { FocusSetting } from 'domains/estimate/Estimate.types';
import { UserRole } from 'domains/user';
import { IconWrapper } from 'pages/CatalogPage/IAM/SubcategorySection/LaborTimeSection/LaborTimeSection.styled';
import { SETTINGS_RATES } from 'pages/SettingsPage/Estimates/EstimateSettings';
import { theme } from 'styles';
import { Flex, Icon, Link, Text } from 'UI';
import { hasData } from 'utils';

interface TableHeaderProps {
  onDeleteAll?: () => void;
  hideTitle?: boolean;
}

const HeaderTextStyle = ({ children }: { children: React.ReactNode }) => {
  return (
    <Text type="heading2" disableGutter>
      {children}
    </Text>
  );
};

const SubheaderTextStyle = ({ children }: { children: React.ReactNode }) => {
  return (
    <Text type={'card_title_light'} disableGutter>
      {children}
    </Text>
  );
};

interface DeleteAllActionProps {
  onClick: () => void;
}

const DeleteAllAction = ({ onClick }: Readonly<DeleteAllActionProps>) => (
  <Flex size={0}>
    <Icon
      IconComponent={TrashAltIcon}
      color={theme.color.clear_blue}
      size={25}
      onClick={onClick}
      data-cy={'delete-all-button'}
    />
  </Flex>
);

export function ReferenceTableHeader({ onDeleteAll, hideTitle }: Readonly<TableHeaderProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleAddReference = (referenceNumber: string) => {
    dispatch(addReferenceNumber({ referenceNumber }));
  };

  return (
    <Flex align={'center'} gap={10}>
      {!hideTitle && (
        <HeaderTextStyle>
          <Trans i18nKey={'catalog.reference_plural'}>{'References'}</Trans>
        </HeaderTextStyle>
      )}
      {onDeleteAll && <DeleteAllAction onClick={onDeleteAll} />}
      <Flex justify={'flex-end'}>
        <ReferenceInput
          size={'middle'}
          placeholder={t('estimate.action.add_known_reference_to_estimate', 'Add reference to your estimate...')}
          onConfirm={handleAddReference}
        />
      </Flex>
    </Flex>
  );
}

export function LaborTimeTableHeader({ onDeleteAll, hideTitle }: Readonly<TableHeaderProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentEstimateId = useSelector(getCurrentEstimateId);
  const vehicle = useSelector((state: RootState) => getEstimateVehicle(state, currentEstimateId));
  const estimateSettings = useSelector(getEstimateSettings);
  const history = useHistory();

  const handleAddLaborTime = (laborTimeCode: string) => {
    dispatch(addCatalogLaborTimeByCode(laborTimeCode));
  };

  return (
    <Flex align={'center'} gap={10}>
      {!hideTitle && (
        <HeaderTextStyle>
          <Trans i18nKey={'catalog.parts.category.car_parts.labor_times'}>{'Labor times'}</Trans>
        </HeaderTextStyle>
      )}
      {onDeleteAll && <DeleteAllAction onClick={onDeleteAll} />}
      {!hasData(estimateSettings) && (
        <Popover
          content={`${t('estimate.action.labor_times.configure_new_settings', 'Configure new settings')}`}
          placement={'bottomLeft'}
          trigger="hover"
        >
          <IconWrapper>
            <Icon
              IconComponent={CogIcon}
              color={theme.color.clear_blue}
              height={22}
              width={23}
              onClick={() => {
                const params = new URLSearchParams();
                params.set(FocusSetting, SETTINGS_RATES);
                history.push(`${ROUTER_SETTINGS_ESTIMATE}?${params}`);
              }}
            />
          </IconWrapper>
        </Popover>
      )}
      {vehicle && hasData(estimateSettings) && (
        <Flex justify={'flex-end'}>
          <ReferenceInput
            size={'middle'}
            placeholder={t('estimate.action.add_labor_time_to_estimate', 'Add a labor time to your estimate...')}
            onConfirm={handleAddLaborTime}
          />
        </Flex>
      )}
    </Flex>
  );
}

export const BundleOrRecyclingTableHeader = ({ onDeleteAll, hideTitle }: TableHeaderProps) => {
  const myStoreEnabled = useSelector(getMyStoreEnabled);

  return (
    <Flex align={'center'} gap={10}>
      {!hideTitle && (
        <HeaderTextStyle>
          <Trans i18nKey={'estimate.bundle_or_recycling.title'}>{'Bundles/Recycling'}</Trans>
        </HeaderTextStyle>
      )}
      {onDeleteAll && <DeleteAllAction onClick={onDeleteAll} />}
      {myStoreEnabled && (
        <PrivateComponent
          requiredRights={[UserRole.R2, UserRole.R3, UserRole.R3_MOTRIO]}
          render={() => (
            <Flex maxHeight={25} size={0}>
              <Link to={ROUTER_MY_STORE_BUNDLES}>
                <Icon IconComponent={CogIcon} color={theme.color.clear_blue} size={25} />
              </Link>
            </Flex>
          )}
        />
      )}
    </Flex>
  );
};

export const OperationReferenceTableHeader = () => {
  return (
    <SubheaderTextStyle>
      <Trans i18nKey={'catalog.reference_plural'}>{'References'}</Trans>
    </SubheaderTextStyle>
  );
};

export const OperationLaborTimesTableHeader = () => {
  return (
    <SubheaderTextStyle>
      <Trans i18nKey={'catalog.parts.category.car_parts.labor_times'}>{'Labor times'}</Trans>
    </SubheaderTextStyle>
  );
};
